/**
 * usePermission — client-side mirror of backend permissionService.can()
 *
 * Usage:
 *   const { can, hasFeature, isOwner } = usePermission()
 *   can('billing:cancel')   // → boolean
 *   hasFeature('ipd')       // → boolean
 *   isOwner                 // → boolean
 *
 * NOTE: This is for UI gating only. Server always re-checks.
 */

import { useCallback } from "react"
import { useAuth } from "@/context/AuthContext"

// ─── Plan feature map (must match backend permissionService.js) ───────────────

export const PLAN_FEATURES: Record<string, Record<string, boolean>> = {
  basic: {
    ipd: false, reports: false, role_access: false,
    peds_calculator: false, drug_interactions: false,
    data_export: false, gst_invoice: false, multi_branch: false,
  },
  clinic: {
    ipd: false, reports: true, role_access: true,
    peds_calculator: false, drug_interactions: false,
    data_export: false, gst_invoice: false, multi_branch: false,
  },
  hospital: {
    ipd: true, reports: true, role_access: true,
    peds_calculator: true, drug_interactions: true,
    data_export: true, gst_invoice: true, multi_branch: false,
  },
  enterprise: {
    ipd: true, reports: true, role_access: true,
    peds_calculator: true, drug_interactions: true,
    data_export: true, gst_invoice: true, multi_branch: true,
  },
}

const MODULE_PLAN_MAP: Record<string, string> = {
  patients:      'patients',
  queue:         'patients',
  prescriptions: 'patients',
  billing:       'patients',
  vitals:        'patients',
  settings:      'patients',
  appointments:  'patients',
  ipd:           'ipd',
  reports:       'reports',
  users:         'role_access',
}

function planHasFeature(plan: string, feature: string): boolean {
  return PLAN_FEATURES[plan]?.[feature] ?? false
}

function legacyRoleCheck(role: string, module: string): boolean {
  if (role === 'admin') return true
  if (role === 'doctor') return ['patients', 'prescriptions', 'queue', 'ipd', 'billing', 'settings', 'appointments'].includes(module)
  if (role === 'staff')  return ['patients', 'queue', 'billing', 'appointments'].includes(module)
  return false
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function usePermission() {
  const { currentUser } = useAuth()

  const tenantPlan = currentUser?.tenant_plan ?? 'hospital'
  const effectivePerms = currentUser?.effective_permissions ?? null

  const can = useCallback((permission: string): boolean => {
    if (!currentUser) return false

    const [module, action] = permission.split(':')

    // 1. Plan gate
    const planFeature = MODULE_PLAN_MAP[module] ?? module
    if (planFeature !== 'patients' && !planHasFeature(tenantPlan, planFeature)) {
      return false
    }

    // 2. Owner bypass
    if (currentUser.is_owner) return true

    // 3. Use effective_permissions from server (preferred — includes overrides + template)
    if (effectivePerms) {
      const allowed = effectivePerms[module] ?? []
      if (action === 'read') return allowed.includes('read') || allowed.includes('write') || allowed.includes('own_only')
      return allowed.includes(action)
    }

    // 4. Fallback — legacy role check (no template data yet)
    return legacyRoleCheck(currentUser.role, module)
  }, [currentUser, tenantPlan, effectivePerms])

  const hasFeature = useCallback((feature: string): boolean => {
    return planHasFeature(tenantPlan, feature)
  }, [tenantPlan])

  return {
    can,
    hasFeature,
    isOwner:    !!currentUser?.is_owner,
    tenantPlan,
  }
}
